import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { isEmpty } from "lodash";

import Alert from "components/shared/common/alert";
import ChargebeeIframe from "components/ChargebeeIframe";
import getTeamApiCall from "components/shared/utils/getTeamApiCall";
import OnboardingModal from "components/heylime-layout/components/OnboardingModal";
import { setIsLoading } from "redux/loader/action";
import { setCurrentUser } from "redux/game/action";
import {
  getBillingHistory,
  listSubscription,
  getChargebeeInvoice,
  updatePromoCode,
  getBlenderCredits,
  retrieveBlenderCredits,
  updateBlenderCredits,
  updatePaymentMethod,
  renewAnuualPlanApi
} from "./api";
import AddOn from "./components/AddOn";
import UpdatePlans from "./components/UpdatePlans";
import PlanDetails from "./components/PlanDetails";
import Notification from "./components/Notification";
import BillingHeader from "./components/BillingHeader";
import PaymentDetails from "./components/PaymentDetails";
import AccountHistory from "./components/AccountHistory";
import SideBarTabs from "../landingLibrary/sideBarTabs";

const Billing = ({ history }) => {
  const search = useLocation().search;
  const dispatch = useDispatch();
  const {
    gameReducer: { currentUser, gamesData }
  } = useSelector((state) => state);

  const teamId = currentUser?.team?.id;
  const teamCount = currentUser?.team?.count;
  const teamLimit = currentUser?.team?.limit;
  const lastPayment = currentUser?.team?.lastPayment;
  const AnnualPlanState = currentUser?.team?.AnnualPlanState;

  const plans_details =
    gamesData.pricing_flow.pricing.trial_ended.from_trial_plans;

  const [chargebeeUrl, setChargebeeUrl] = useState("");
  const [billingHistory, setBillingHistory] = useState({});
  const [creditsQuantity, setCreditsQuantity] = useState("");
  const [renewAnnualPlan, setRenewAnnualPlan] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({ list: [] });
  const [blenderCreditsModal, setBlenderCreditsModal] = useState(false);
  const [annualPlanNotification, setAnnualPlanNotification] = useState(false);

  const handleIsLoading = (value) => dispatch(setIsLoading(value));
  const toggleRenewAnnualPlan = () => setRenewAnnualPlan((prev) => !prev);
  const toggleCreditsModal = () => setBlenderCreditsModal((prev) => !prev);

  const formattedDate = () => {
    const date = new Date(AnnualPlanState);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  const did_mount = async () => {
    handleIsLoading(true);

    let listPayload = { team_code: teamId };
    if (AnnualPlanState != null) {
      listPayload.state = "non_renewing";
    }
    const paymentDetailsRes = await listSubscription(listPayload);

    if (paymentDetailsRes.list) {
      setPaymentDetails(paymentDetailsRes);
    } else {
      const { amount, quantity, unit_price, item_price_id } =
        paymentDetailsRes.subscription_items[0];
      setPaymentDetails({
        amount: amount,
        quantity: quantity,
        unit_price: unit_price,
        id: paymentDetailsRes.id,
        item_price_id: item_price_id,
        next_billing_at: paymentDetailsRes.next_billing_at
      });
    }
    if (search !== "") {
      const queryParams = new URLSearchParams(search);
      const id = queryParams.get("id");
      const state = queryParams.get("state");
      if (id !== null && state === "succeeded") {
        history.replace("billing");
        const invoiceDetails = await getChargebeeInvoice(id);
        if ("state" in invoiceDetails && state === "succeeded") {
          if ("company" in invoiceDetails) {
            const plan = plans_details.find(
              (item) => item.planPriceId === invoiceDetails.plan
            );
            const data = await updatePromoCode({
              promoCode: teamId,
              limit: teamCount,
              end_date:
                currentUser.plan_name === plan.planPriceId
                  ? 0
                  : plan.planPriceId === "Platform-Only-USD-Monthly"
                  ? 30
                  : 365,
              plan: plan.planPriceId,
              prev_plan:
                currentUser.plan_name === plan.planPriceId
                  ? null
                  : currentUser.plan_name,
              cost: plan.Price.toFixed(2),
              quantity: teamCount,
              total_amount: (plan.Price * teamCount).toFixed(2)
            });
            if (data.message === "Promo code updated sucessfully") {
              const team = await getTeamApiCall(teamId, false);
              dispatch(
                setCurrentUser({
                  ...currentUser,
                  plan_name: plan.planPriceId,
                  team: team || {}
                })
              );
            }
          } else {
            const blenderCeditsRes = await retrieveBlenderCredits(teamId);
            const team = await updateBlenderCredits({
              team_code: teamId,
              plan: "Event-only-USD",
              quantity: blenderCeditsRes[0].quantity
            });
            dispatch(
              setCurrentUser({
                ...currentUser,
                team: team || {}
              })
            );
          }
        }
      }
    }
    const billingHistoryRes = await getBillingHistory(teamId);
    setBillingHistory(billingHistoryRes);
    handleIsLoading(false);
  };

  useEffect(() => {
    if (localStorage.getItem("annualNotification"))
      setAnnualPlanNotification(true);
  }, []);

  const onNotificationClose = () => {
    setAnnualPlanNotification(false);
    localStorage.removeItem("annualNotification");
  };

  useEffect(() => {
    if (!isEmpty(currentUser.team)) did_mount();
  }, [search, JSON.stringify(currentUser)]);

  const purchaseCredits = async () => {
    if (!isEmpty(creditsQuantity)) {
      toggleCreditsModal();
      handleIsLoading(true);
      const urlData = await getBlenderCredits({
        id: teamId,
        item_price_id: "Event-only-USD",
        quantity: parseInt(creditsQuantity)
      });
      if (!isEmpty(urlData.hosted_page.url))
        setChargebeeUrl(urlData.hosted_page.url);

      handleIsLoading(false);
    }
  };

  const onUpdatePaymentMethod = async () => {
    try {
      const urlData = await updatePaymentMethod(teamId);
      if (!isEmpty(urlData.hosted_page.url))
        setChargebeeUrl(urlData.hosted_page.url);
    } catch (error) {
      console.log("Error while fetching chargebee url: ", error);
    }
  };

  const onRenewAnuualPlan = async () => {
    try {
      handleIsLoading(true);
      const renewPlanRes = await renewAnuualPlanApi({
        subscription_id: paymentDetails.id
      });
      if (renewPlanRes?.subscription?.status === "active") {
        onNotificationClose();
        const team = await getTeamApiCall(teamId, false);
        await dispatch(
          setCurrentUser({
            ...currentUser,
            team: team || {}
          })
        );
      }
      toggleRenewAnnualPlan();
    } catch (error) {
      Alert(400, error.message);
    } finally {
      handleIsLoading(false);
    }
  };

  return (
    <Row className="team-ch-row">
      <Col md={1} lg={1} className="p-0">
        <SideBarTabs
          current_user={currentUser}
          handleOptClick={() => history.push("/outside_team_building")}
        />
      </Col>
      <Col md={11} lg={11}>
        <main className="billing-main">
          <BillingHeader />
          {lastPayment && lastPayment === "Failure" && (
            <Notification
              btnText="Update Payment"
              headerText="Payment Failed"
              title="Update Payment Method"
              onClick={onUpdatePaymentMethod}
              desc="Our last attempt to collect your team’s plan payment was unsuccessful. Please review or update your payment method to continue using your plan. If continue having issues, please contact our support team at "
            />
          )}
          {annualPlanNotification && (
            <Notification
              btnText="Close"
              btnText2="Renew"
              headerText="Notification"
              title="Annual Plan Change"
              onClick={onNotificationClose}
              onClick2={toggleRenewAnnualPlan}
              desc={`Your team manager recently canceled your team’s annual plan, and therefore it will not renew your subscription. Your team can continue enjoying the Perform - Yearly’s benefits until ${formattedDate()}. You will also still have access to your remaining Blender Credits until ${formattedDate()}. If have any questions, please contact our support team at `}
            />
          )}
          <Row>
            <Col md={8} lg={8}>
              <PlanDetails
                history={history}
                teamLimit={teamLimit}
                teamCount={teamCount}
                paymentDetails={paymentDetails}
                blenderCredits={currentUser?.team?.blenderCredits}
              />
            </Col>
            <Col md={4} lg={4}>
              <PaymentDetails paymentDetails={paymentDetails} />
            </Col>
            <Col md={8} lg={8}>
              <AddOn
                onAddCredits={toggleCreditsModal}
                blenderCredits={currentUser?.team?.blenderCredits}
              />
            </Col>
            <Col md={4} lg={4}>
              <UpdatePlans
                history={history}
                currentUser={currentUser}
                paymentDetails={paymentDetails}
                onUpdatePaymentMethod={onUpdatePaymentMethod}
                cancelPlanNotification={() => setAnnualPlanNotification(true)}
              />
            </Col>
          </Row>
          <AccountHistory billingHistory={billingHistory} />
        </main>
      </Col>

      <OnboardingModal
        inputType="number"
        btnText1="Continue"
        title="Add Blender Credits"
        toggle={toggleCreditsModal}
        btnClick1={purchaseCredits}
        isOpen={blenderCreditsModal}
        inputValue={creditsQuantity}
        inputOnChange={(e) => setCreditsQuantity(e.target.value)}
        desc="Enter the amount of Blender Credits you need to purchase."
      />

      <OnboardingModal
        inputType=""
        btnText1="OK"
        btnText2="Cancel"
        inputDisabled={true}
        isOpen={renewAnnualPlan}
        title="Renew Annual Plan"
        btnClick1={onRenewAnuualPlan}
        toggle={toggleRenewAnnualPlan}
        btnClick2={toggleRenewAnnualPlan}
        desc="Are you sure you want to re-activate the renewal of your Annual Plan?"
      />

      {chargebeeUrl !== "" && (
        <ChargebeeIframe
          url={chargebeeUrl}
          onCLose={() => setChargebeeUrl("")}
        />
      )}
    </Row>
  );
};

export default Billing;
